/**
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 */

import ox from '@/ox'
import _ from '@/underscore'

import DocConverterUtils from '@/io.ox/core/tk/doc-converter-utils'
import capabilities from '@/io.ox/core/capabilities'
import mailApi from '@/io.ox/mail/api'
import countApi from '@/io.ox/core/api/count'

let viewedFiles = {}

function getCachedXmlProfile (model) {
  // Depending on how the viewer was opened the baton/model can look different, e.g. context menu,
  // clicking on the preview. Try the best to get a possible cached xmlProfile, but always fail gracefully
  // to ensure a error free operation in any situation.
  try {
    let cachedXmlProfile
    if (model.isComposeAttachment()) {
      cachedXmlProfile = model.get('origData')?._xmlProfile
    } else if (model.isMailAttachment()) {
      // the model in a baton can be outdated because it's often a copy of the model, thus get it from the pool
      const latestModel = mailApi.pool.get('detail').get(_.cid(model.attributes.origData.mail))
      const currenttFile = latestModel.get('attachments').find(attachment => attachment.id === model.get('id'))
      cachedXmlProfile = currenttFile._xmlProfile
    }
    const isValidXmlProfile = typeof cachedXmlProfile === 'object' && Object.hasOwn(cachedXmlProfile, 'state') && Object.hasOwn(cachedXmlProfile, 'value')
    return isValidXmlProfile ? cachedXmlProfile : null
  } catch (er) {
    return null
  }
}

export function clearFileViewCount () {
  viewedFiles = {}
}

export async function countFileView (model) {
  if (countApi.disabled) return
  try {
    // a file should count only one time per Viewer lifetime
    if (viewedFiles[model.get('cid')]) return
    viewedFiles[model.get('cid')] = 'true'

    // file extensions must not directly be passed through for security, getFileType provides mapped strings
    let fileType = model.getFileType()

    if (fileType === 'xml' && capabilities.has('document_preview_xrechnung')) {
      // _xmlProfile can exist when it was loaded on demand before e.g. in Mail attachments
      const xmlProfile = getCachedXmlProfile(model)
      if (xmlProfile?.state === 'done') {
        fileType = xmlProfile.value === 'xrechnung' ? 'xrechnung' : 'xml'
      } else {
        // delay the request to not interfere with actual viewer requests
        await new Promise(resolve => setTimeout(resolve, 500))
        const data = await DocConverterUtils.getDocumentFormat(model)
        if (data?.errorData) throw new Error(`XML profile detection error: ${JSON.stringify(data)}`)
        fileType = data.profile === 'xrechnung' ? 'xrechnung' : 'xml'
      }
    } else if (model.isRfc822Attachment()) {
      fileType = 'email'
    } else if (typeof fileType !== 'string') {
      fileType = 'unsupported'
    }

    countApi.add('event', { c: 'viewer-show-file-type', e: fileType })
  } catch (error) {
    if (ox.debug) console.error(error)
  }
}
